import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Input, Select } from 'antd';
import {
  getSupportedRegionCodes,
  parsePhoneNumber,
  getCountryCodeForRegionCode,
} from 'awesome-phonenumber';
import codesToData from './region-code.json';
import findKey from 'lodash/findKey';

const regionList = getSupportedRegionCodes()
  .map((code) => codesToData[code])
  .sort((a, b) => a.name.localeCompare(b.name));

const getCountryCode = (defaultCountry) => {
  if (defaultCountry.length === 2) {
    return findKey(codesToData, { code: defaultCountry });
  }
  return findKey(codesToData, { name: defaultCountry });
};

const PhoneInput = ({
  value,
  defaultCountry = 'AM',
  placeholder,
  onChange = (value: any) => {},
  disabled,
}: any) => {
  const [phoneNumber, setPhoneNumber] = useState(
    value ? `+${value}` : undefined,
  );

  const [country, setCountry] = useState<string>(
    getCountryCode(defaultCountry)?.toLocaleUpperCase() || 'AM',
  );
  const [open, setOpen] = useState(false);
  const input = useRef(null);
  const isFirstTime = useRef(true);

  const pn: any = useMemo(
    () => parsePhoneNumber(phoneNumber || '', { regionCode: country }),
    [phoneNumber, country],
  );

  const countryCode = useMemo(() => getCountryCodeForRegionCode(country), [
    country,
  ]);
  const internationalNumber = useMemo(
    () =>
      (pn.number?.international || '').replace(`+${countryCode} `, '') ||
      phoneNumber,
    [countryCode, phoneNumber],
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setPhoneNumber(`+${value}`);
  }, [value]);

  useEffect(() => {
    if (pn.regionCode) {
      setCountry(pn.regionCode);
    }
    if (isFirstTime.current) {
      isFirstTime.current = false;
      return;
    }
    onChange(pn);
  }, [phoneNumber]);

  const handleFocus = useCallback(() => {
    setOpen(true);
  }, []);
  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);
  const handleSelect = useCallback((code) => {
    setCountry(code);
    setOpen(false);
    input.current.focus();
  }, []);
  const handleChange = useCallback((e) => {
    setPhoneNumber(e.target.value);
  }, []);

  const width = open ? 170 : 80;

  return (
    <Input.Group compact>
      <Select
        disabled={disabled}
        style={{ transition: 'width 0.3s ease-out', width }}
        showSearch
        value={country}
        optionLabelProp="label"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelect}
        filterOption={(input, option) =>
          option.props.text.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        dropdownMatchSelectWidth={170}
      >
        {regionList.map((region) => (
          <Select.Option
            key={region.code}
            label={region.emoji}
            text={`${region.code} ${region.name}`}
            value={region.code}
          >
            {region.emoji} {region.name}
          </Select.Option>
        ))}
      </Select>

      <Input
        disabled={disabled}
        ref={input}
        prefix={<span style={{ color: '#ccc' }}>{`+${countryCode}`}</span>}
        style={{
          transition: 'width 0.3s ease-out',
          width: `calc(100% - ${width}px)`,
          alignItems: 'center',
        }}
        value={internationalNumber}
        onChange={handleChange}
        placeholder={placeholder}
      />
    </Input.Group>
  );
};

export default PhoneInput;
